'use client';

import { c as _c } from "react/compiler-runtime";
import { Trash } from '@byway/icons';
import Image from 'next/image';
import { useFormatter, useLocale, useTranslations } from 'next-intl';
import React, { type ReactNode } from 'react';
import ContentLoader from 'react-content-loader';
import { Button, Heading, Stack, Text } from 'ui';
import { parseDateAsUtc } from 'utils/DateFormats';
import { Card } from '~/components/Card';
import { Disclosure } from '~/components/Disclosure';
import { Link } from '~/components/Link';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useResolvedTripTitle } from '~/hooks/useTripDisplayTitle';
import type { Locale } from '~/i18n/i18n-config';
import { getPathname } from '~/i18n/navigation';
import { api } from '~/libs/api';
import { formatFixedAmount } from '~/utils/currencyHelpers';
import { resolveTripIntroText } from '~/utils/tripTitle';
import { Notification } from '../Notification';
const FALLBACK_IMG_SRC = 'https://uploads-ssl.webflow.com/5e709c90bf3d8e4bba851ecb/61ab60b64e4e0f10a58bd541_cordobadoor1.jpg';
type CardProps = {
  tripId: string;
  createdAt?: string;
  variant?: 'saved' | 'booked';
};
function ButtonLoader() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ContentLoader className="w-full h-10 rounded" uniqueKey="button"><rect width="100%" height="100%" /></ContentLoader>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function CardLoader() {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ContentLoader className="w-full h-48" uniqueKey="header"><rect width="100%" height="100%" /></ContentLoader>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ContentLoader className="w-3/4 h-8 rounded" uniqueKey="header"><rect width="100%" height="100%" /></ContentLoader>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ContentLoader className="w-full h-6 rounded" uniqueKey="header"><rect width="100%" height="100%" /></ContentLoader>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex-1 p-4 space-y-4">{t1}{t2}<ContentLoader className="w-full h-6 rounded" uniqueKey="header"><rect width="100%" height="100%" /></ContentLoader></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col w-full h-full overflow-hidden border rounded">{t0}{t3}<div className="p-5 space-y-2"><ButtonLoader /><ButtonLoader /></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}

// Split out so the title hooks run only once the trip has loaded (SharedTripCard returns early).
function TripCardTitle(t0) {
  const $ = _c(2);
  const {
    trip
  } = t0;
  const t1 = useResolvedTripTitle(trip);
  let t2;
  if ($[0] !== t1) {
    t2 = <Heading as="h3" variant="h6">{t1}</Heading>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function TripCardIntro(t0) {
  const $ = _c(6);
  const {
    trip
  } = t0;
  const t = useTranslations();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== t || $[2] !== trip) {
    t1 = resolveTripIntroText(t, locale, trip);
    $[0] = locale;
    $[1] = t;
    $[2] = trip;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <p className="py-3">{t1}</p>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function SharedTripCard(t0) {
  const $ = _c(53);
  const {
    tripId,
    children,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "saved" : t1;
  const t = useTranslations();
  const format = useFormatter();
  const locale = useLocale() as Locale;
  let t2;
  if ($[0] !== locale || $[1] !== tripId) {
    t2 = {
      tripId,
      locale
    };
    $[0] = locale;
    $[1] = tripId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data,
    error
  } = api.trip.get.useQuery(t2, t3);
  if (error && !data) {
    return null;
  }
  if (!data) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <CardLoader />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  const separateNotificationAndContent = _temp3;
  let t4;
  if ($[5] !== children || $[6] !== data) {
    t4 = typeof children === "function" ? children(data) : children;
    $[5] = children;
    $[6] = data;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const childContent_0 = t4;
  let t5;
  if ($[8] !== childContent_0) {
    t5 = separateNotificationAndContent(childContent_0);
    $[8] = childContent_0;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    notification: notification_0,
    remainingContent
  } = t5;
  const t6 = data.header_image?.src || FALLBACK_IMG_SRC;
  const t7 = data.header_image?.alt ?? "";
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <div className="overflow-hidden relative flex-shrink-0 h-48"><Image src={t6} fill={true} sizes="(min-width: 768px) 350px, 100vw" alt={t7} className="object-cover" /></div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== data) {
    t9 = <TripCardTitle trip={data} />;
    $[13] = data;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== data.details.startDate || $[16] !== format) {
    t10 = format.dateTime(parseDateAsUtc(data.details.startDate), {
      timeZone: "UTC",
      day: "numeric",
      month: "short"
    });
    $[15] = data.details.startDate;
    $[16] = format;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== data.details.endDate || $[19] !== format) {
    t11 = format.dateTime(parseDateAsUtc(data.details.endDate), {
      timeZone: "UTC",
      day: "numeric",
      month: "short",
      year: "numeric"
    });
    $[18] = data.details.endDate;
    $[19] = format;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = <Text variant="emphasis-1">{t10}{" "}-{" "}{t11}</Text>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== data.details.tripInDays || $[25] !== t) {
    t13 = t("journey.days_count", {
      count: data.details.tripInDays
    });
    $[24] = data.details.tripInDays;
    $[25] = t;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <Text variant="emphasis-1" className="text-neutral-700">{t13}</Text>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t12 || $[30] !== t14) {
    t15 = <Stack className="mt-2" spacing={1}>{t12}{t14}</Stack>;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15 || $[33] !== t9) {
    t16 = <div className="flex-shrink-0 p-3">{t9}{t15}</div>;
    $[32] = t15;
    $[33] = t9;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== tripId || $[36] !== variant) {
    t17 = variant === "booked" && <TripBalanceCard tripId={tripId} />;
    $[35] = tripId;
    $[36] = variant;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== data) {
    t18 = <TripCardIntro trip={data} />;
    $[38] = data;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== notification_0 || $[41] !== t17 || $[42] !== t18) {
    t19 = <div className="flex-1 p-3 space-y-2">{notification_0}{t17}{t18}</div>;
    $[40] = notification_0;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== remainingContent) {
    t20 = <div className="p-3">{remainingContent}</div>;
    $[44] = remainingContent;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t19 || $[47] !== t20) {
    t21 = <div className="flex flex-col flex-1">{t19}{t20}</div>;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t16 || $[50] !== t21 || $[51] !== t8) {
    t22 = <div className="flex flex-col h-full overflow-hidden border rounded">{t8}{t16}{t21}</div>;
    $[49] = t16;
    $[50] = t21;
    $[51] = t8;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  return t22;
}
function _temp3(childContent) {
  if (!React.isValidElement(childContent)) {
    return {
      notification: null,
      remainingContent: childContent
    };
  }
  if (React.isValidElement(childContent) && childContent.type === React.Fragment) {
    const fragment = childContent as React.ReactElement<any, any>;
    const children_0 = React.Children.toArray(fragment.props.children);
    const notification = children_0.find(_temp);
    const remaining = children_0.filter(_temp2);
    return {
      notification,
      remainingContent: remaining
    };
  }
  if (childContent.type === Notification) {
    return {
      notification: childContent,
      remainingContent: null
    };
  }
  return {
    notification: null,
    remainingContent: childContent
  };
}
function _temp2(child_0) {
  return React.isValidElement(child_0) && child_0.type !== Notification;
}
function _temp(child) {
  return React.isValidElement(child) && child.type === Notification;
}
function SavedTripCard(t0) {
  const $ = _c(10);
  const {
    tripId
  } = t0;
  const t = useTranslations("account");
  const locale = useLocale();
  const removeTrip = api.account.removeTrip.useMutation();
  const utils = api.useUtils();
  let t1;
  if ($[0] !== locale || $[1] !== removeTrip || $[2] !== t || $[3] !== tripId || $[4] !== utils.account.isTripSaved || $[5] !== utils.account.savedTrips) {
    t1 = data => <><Button is="a" block={true} href={getPathname({
        href: tripId ? `/journey/${tripId}` : `/journey-template/${data.uniqueId}`,
        locale
      })}>{t("view_journey")}</Button><Button type="tertiary" block={true} onClick={async () => {
        await removeTrip.mutateAsync({
          tripId
        });
        await utils.account.isTripSaved.invalidate({
          tripId
        });
        utils.account.savedTrips.invalidate();
      }}><Stack spacing={1} horizontal={true} align="center"><Trash decorative={true} /><span>{t("remove_journey")}</span></Stack></Button></>;
    $[0] = locale;
    $[1] = removeTrip;
    $[2] = t;
    $[3] = tripId;
    $[4] = utils.account.isTripSaved;
    $[5] = utils.account.savedTrips;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1 || $[8] !== tripId) {
    t2 = <SharedTripCard variant="saved" tripId={tripId}>{t1}</SharedTripCard>;
    $[7] = t1;
    $[8] = tripId;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function BookedTripCard(t0) {
  const $ = _c(31);
  const {
    tripId,
    createdAt
  } = t0;
  const t = useTranslations("account");
  const t1 = createdAt || "";
  let t2;
  if ($[0] !== t1 || $[1] !== tripId) {
    t2 = {
      tripId,
      createdAt: t1
    };
    $[0] = t1;
    $[1] = tripId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data
  } = api.trip.tripStatus.useQuery(t2);
  let t3;
  if ($[3] !== t) {
    t3 = t("trip_summary_pending");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <Notification state="info" message={t3} hideCloseButton={true} />;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("view_journey_summary");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <Button is="button" disabled={true} block={true}>{t5}</Button>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = `/journey-guide/${tripId}?summary=true`;
  let t9;
  if ($[14] !== t) {
    t9 = t("view_journey_summary");
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <Button is="a" block={true} href={t8}>{t9}</Button>;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = `/journey-guide/${tripId}`;
  let t12;
  if ($[19] !== t) {
    t12 = t("view_journey_guide");
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11 || $[22] !== t12) {
    t13 = <Button is="a" block={true} href={t11}>{t12}</Button>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t10 || $[25] !== t13 || $[26] !== t7) {
    t14 = {
      SHOW_ON_HOLD_STATE: t7,
      SHOW_JOURNEY_SUMMARY: t10,
      SHOW_JOURNEY_GUIDE: t13
    };
    $[24] = t10;
    $[25] = t13;
    $[26] = t7;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const CTAs = t14;
  const t15 = CTAs[data?.status || "SHOW_ON_HOLD_STATE"];
  let t16;
  if ($[28] !== t15 || $[29] !== tripId) {
    t16 = <SharedTripCard variant="booked" tripId={tripId}>{t15}</SharedTripCard>;
    $[28] = t15;
    $[29] = tripId;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
}
interface TripBalanceCardProps {
  tripId: string;
}
function TripBalanceCard(t0) {
  const $ = _c(67);
  const {
    tripId
  } = t0;
  const t = useTranslations("account");
  const locale = useLocale() as Locale;
  const {
    data,
    error
  } = api.account.balance.useQuery(tripId);
  let t1;
  if ($[0] !== locale) {
    t1 = amount => formatFixedAmount(locale, "GBP", amount, true);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formatGBP = t1;
  if (error || !data) {
    return null;
  }
  const {
    outstandingBalance,
    paidAlready
  } = data;
  let t2;
  if ($[2] !== t) {
    t2 = t("trip_amount_paid");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <p className="text-xs">{t2}</p>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== formatGBP || $[7] !== paidAlready) {
    t4 = formatGBP(paidAlready);
    $[6] = formatGBP;
    $[7] = paidAlready;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <Text className="text-xl typography-subtitle-2">{t4}</Text>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <Stack justify="between">{t3}{t5}</Stack>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("trip_amount_outstanding");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <p className="text-xs">{t7}</p>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== formatGBP || $[19] !== outstandingBalance) {
    t9 = formatGBP(outstandingBalance);
    $[18] = formatGBP;
    $[19] = outstandingBalance;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <Text className="text-xl typography-subtitle-2">{t9}</Text>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <Stack justify="between">{t8}{t10}</Stack>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("trip_total_cost");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <p className="text-xs">{t12}</p>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = outstandingBalance + paidAlready;
  let t15;
  if ($[30] !== formatGBP || $[31] !== t14) {
    t15 = formatGBP(t14);
    $[30] = formatGBP;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t15) {
    t16 = <Text className="text-xl typography-subtitle-2">{t15}</Text>;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t13 || $[36] !== t16) {
    t17 = <Stack justify="between">{t13}{t16}</Stack>;
    $[35] = t13;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t11 || $[39] !== t17 || $[40] !== t6) {
    t18 = <Stack className="p-3" spacing={2}>{t6}{t11}{t17}</Stack>;
    $[38] = t11;
    $[39] = t17;
    $[40] = t6;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("pay_bank_transfer");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t) {
    t20 = t("bank_details_name", {
      value: "Byway Travel Limited"
    });
    $[44] = t;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <br />;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t) {
    t22 = t("bank_details_sort_code", {
      value: "04-00-75"
    });
    $[47] = t;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <br />;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t) {
    t24 = t("bank_details_account", {
      value: "96476710"
    });
    $[50] = t;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t19 || $[53] !== t20 || $[54] !== t22 || $[55] !== t24) {
    t25 = <div className="m-3"><Disclosure title={t19} showChevron={true}>{t20} {t21}{t22}{t23}{t24}</Disclosure></div>;
    $[52] = t19;
    $[53] = t20;
    $[54] = t22;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t) {
    t26 = t("pay_website");
    $[57] = t;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t26) {
    t27 = <Link href="https://buy.stripe.com/4gwaGWbQGbV6gc8cMY" isExternal={true} className="mx-3 mb-3"><Button className="w-full">{t26}</Button></Link>;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t25 || $[62] !== t27) {
    t28 = <Stack>{t25}{t27}</Stack>;
    $[61] = t25;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t18 || $[65] !== t28) {
    t29 = <Card hasBorder={true} rounded="--by-border-radius-200" spaceHorizontal="--by-space-400" spaceVertical="--by-space-300">{t18}{t28}</Card>;
    $[64] = t18;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  return t29;
}
export { SavedTripCard, BookedTripCard, SharedTripCard };